import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { TplManagementHubData } from '../../services/tplManagementHubInsights';
import { TPL_SEVERITY_STYLE } from '../../services/tplSeverityStyles';

export interface DataflowAttentionItem {
  id: string;
  label: string;
  detail: string;
  severity: 'critico' | 'atencao' | 'informativo';
}

interface DataflowAttentionNowProps {
  hub?: TplManagementHubData | null;
  extraItems?: DataflowAttentionItem[];
}

const SEVERITY_ORDER = { critico: 0, atencao: 1, informativo: 2 };

function buildItemsFromHub(hub: TplManagementHubData): DataflowAttentionItem[] {
  const items: DataflowAttentionItem[] = [];

  for (const risk of hub.risks.slice(0, 3)) {
    items.push({
      id: `risk-${risk.id}`,
      label: risk.label,
      detail: 'Risco ativo na operação',
      severity: risk.tone === 'critico' ? 'critico' : 'atencao',
    });
  }

  for (const leader of hub.criticalLeaders.slice(0, 3)) {
    items.push({
      id: `leader-${leader.id}`,
      label: `Líder crítico: ${leader.name}`,
      detail: `Disciplina ${leader.disciplinaPct}% · Conformidade ${leader.conformidadePct}%`,
      severity: leader.tone === 'critico' ? 'critico' : 'atencao',
    });
  }

  for (const chain of hub.escalatedChains.slice(0, 3)) {
    items.push({
      id: `esc-${chain.leaderId}`,
      label: `Escalonamento: ${chain.leaderName}`,
      detail: `${chain.statusLabel} · ${chain.timeOpenLabel}`,
      severity: chain.tone === 'critico' ? 'critico' : 'atencao',
    });
  }

  for (const conflict of hub.plannerConflicts.slice(0, 3)) {
    items.push({
      id: conflict.id,
      label: `Conflito de agenda — ${conflict.leaderName}`,
      detail: `${conflict.dayLabel} ${conflict.hour} · ${conflict.routineTitles.join(', ')}`,
      severity: conflict.priority === 'critico' ? 'critico' : 'atencao',
    });
  }

  if (hub.executiveContext.criticalLossCount > 0) {
    items.push({
      id: 'critical-losses',
      label: 'Perdas críticas no período',
      detail: `${hub.executiveContext.criticalLossCount} ocorrência(s) em rotinas críticas`,
      severity: hub.executiveContext.criticalLossCount >= 3 ? 'critico' : 'atencao',
    });
  }

  return items;
}

/** Nível 3 — O que exige atenção agora (ordenado por severidade). */
export const DataflowAttentionNow: React.FC<DataflowAttentionNowProps> = ({ hub, extraItems = [] }) => {
  const items = useMemo(() => {
    const fromHub = hub ? buildItemsFromHub(hub) : [];
    const merged = [...fromHub, ...extraItems];
    const seen = new Set<string>();
    const deduped: DataflowAttentionItem[] = [];
    for (const item of merged) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      deduped.push(item);
    }
    return deduped.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]).slice(0, 8);
  }, [hub, extraItems]);

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/20">
      <div className="flex items-center gap-1.5 border-b border-amber-100 px-3 py-2 dark:border-amber-900/30">
        <AlertTriangle size={13} className="text-amber-600" />
        <h4 className="text-[10px] font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200">
          O que exige atenção agora
        </h4>
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-4 text-[11px] text-emerald-700 dark:text-emerald-400">
          Nenhuma exceção prioritária no momento.
        </p>
      ) : (
        <ol className="divide-y divide-amber-100 dark:divide-amber-900/20">
          {items.map((item, i) => {
            const style = TPL_SEVERITY_STYLE[item.severity];
            return (
              <li key={item.id} className={`flex items-start gap-2 px-3 py-2 ${style.bg}`}>
                <span className="mt-0.5 w-4 shrink-0 text-[10px] font-bold tabular-nums text-slate-400">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">
                    {item.label}
                  </p>
                  <p className={`text-[10px] ${style.text}`}>{item.detail}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default DataflowAttentionNow;
